import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { formatFeedbackText } from "../../utils/feedbackFormatter";
import nexticn from "../../Components/Assets/svg/chevron-right-solid.svg";
import backicn from "../../Components/Assets/svg/back-chevron-right-solid (1).svg";
// Platform icons
import x from "../../Components/Assets/Management/x-task.png";
import fb from "../../Components/Assets/Management/fb-task.png";
import li from "../../Components/Assets/Management/linkedin-task.png";
import insta from "../../Components/Assets/Management/instagram-task.png";
import mail from "../../Components/Assets/Management/email-icn.png";

// Helper function to format dates for display
const formatDateForDisplay = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toISOString().split("T")[0];
};

/**
 * Approved Task Section - displays and manages approved tasks ready for launch
 */
export function ApprovedTaskSection({
  companyProfile,
  showSecondHalf,
  showCompletedTaskStatus,
  onTaskStatusChange
}) {
  const approvedTasksScrollRef = useRef(null);
  
  // Component state
  const [approvedTasks, setApprovedTasks] = useState([]); // Approved tasks from API
  const [selectedRolledBackTask, setSelectedRolledBackTask] = useState(null); // Task for rollback modal
  const [hasApprovedTasks, setHasApprovedTasks] = useState(false); // Tasks display flag
  const [isLoading, setIsLoading] = useState(false); // Loading state

  // Utility functions
  const getStatusClass = (status) => {
    switch (status) {
      case "Approved":
        return { backgroundColor: "#5eb35eb7" };
      case "Pending":
        return { backgroundColor: "rgba(255, 145, 0, 0.69)" };
      case "Roll Back":
        return { backgroundColor: "rgba(245, 65, 0, 0.69)" };
      case "Posted":
        return { backgroundColor: "rgba(172, 5, 255, 0.76)" };
      default:
        return {};
    }
  };

  // Helper function to format recurrence pattern display
  const formatRecurrencePattern = (recurrence) => {
    if (!recurrence) return "";

    if (recurrence.recurrenceType === "monthly") {
      const week = recurrence.monthlyWeek || "first";
      const dayOfWeek = recurrence.monthlyDayOfWeek || "monday";

      const formattedDay =
        dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1);
      const formattedWeek =
        week === "first"
          ? "1st"
          : week === "second"
          ? "2nd"
          : week === "third"
          ? "3rd"
          : week === "fourth"
          ? "4th"
          : "last";

      return `Every month on the ${formattedWeek} ${formattedDay}`;
    } else if (recurrence.recurrenceType === "weekly") {
      const dayOfWeek = recurrence.weeklyDayOfWeek || "monday";
      const formattedDay =
        dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1);
      return `Every ${formattedDay}`;
    } else if (recurrence.recurrenceType === "bi-weekly") {
      const dayOfWeek = recurrence.weeklyDayOfWeek || "monday";
      const formattedDay =
        dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1);
      return `Every other ${formattedDay}`;
    }

    return recurrence.recurrenceType || "";
  };

  // Data fetching functions
  const fetchApprovedTasks = async () => {
    if (isLoading) {
      console.log("ApprovedTaskSection: Already loading, skipping...");
      return;
    }

    try {
      setIsLoading(true);
      
      if (!companyProfile?._id) {
        console.log("ApprovedTaskSection: No company profile ID available");
        return;
      }

      const token = localStorage.getItem("token");
      if (!token) {
        console.error("ApprovedTaskSection: No authentication token found");
        toast.error("Please log in again", {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
        });
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        console.error("ApprovedTaskSection: REACT_APP_API_URL is not defined");
        toast.error("Configuration error. Please contact support.", {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
        });
        return;
      }

      console.log("ApprovedTaskSection: Fetching tasks for company:", companyProfile._id);
      const response = await axios.get(
        `${apiUrl}/task/tasks/${companyProfile._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.data || !Array.isArray(response.data)) {
        console.error("ApprovedTaskSection: Invalid response data:", response.data);
        toast.error("Invalid data received from server", {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
        });
        return;
      }

      const approvedTasksData = response.data.filter(
        (task) => task.currentStatus === "Approved"
      );
      
      console.log("ApprovedTaskSection: Successfully fetched", approvedTasksData.length, "approved tasks");
      setApprovedTasks(approvedTasksData);
      setHasApprovedTasks(approvedTasksData.length > 0);
    } catch (error) {
      console.error("ApprovedTaskSection: Error fetching approved tasks:", error);
      console.error("ApprovedTaskSection: Error response:", error.response?.data);
      console.error("ApprovedTaskSection: Error status:", error.response?.status);
      
      if (error.response?.status === 401) {
        toast.error("Session expired. Please log in again", {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
        });
      } else if (error.response?.status === 404) {
        console.log("ApprovedTaskSection: No tasks found for company");
        setApprovedTasks([]);
        setHasApprovedTasks(false);
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        toast.error("Network error. Please check your connection.", {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
        });
      } else {
        toast.error(`Error loading approved tasks: ${error.response?.data?.message || error.message}`, {
          className: "gradient-toast",
          bodyClassName: "gradient-toast-body",
          progressClassName: "gradient-toast-progress",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Effect to fetch approved tasks when component mounts or companyProfile changes
  useEffect(() => {
    if (companyProfile?._id && !isLoading) {
      console.log("ApprovedTaskSection: useEffect triggered, fetching tasks...");
      fetchApprovedTasks();
    }
  }, [companyProfile?._id]); // Only depend on the ID, not the whole object

  // Refetch on global task update events
  useEffect(() => {
    const onTasksUpdated = () => {
      if (!isLoading && companyProfile?._id) {
        fetchApprovedTasks();
      }
    };
    window.addEventListener('hussme:tasks-updated', onTasksUpdated);
    return () => window.removeEventListener('hussme:tasks-updated', onTasksUpdated);
  }, [companyProfile?._id, isLoading]);

  // Scroll functions for approved tasks horizontal navigation
  const scrollApprovedTasksLeft = () => {
    if (approvedTasksScrollRef.current) {
      approvedTasksScrollRef.current.scrollBy({
        left: -350,
        behavior: "smooth",
      });
    }
  };

  const scrollApprovedTasksRight = () => {
    if (approvedTasksScrollRef.current) {
      approvedTasksScrollRef.current.scrollBy({
        left: 350,
        behavior: "smooth",
      });
    }
  };

  // Task detail handler
  const completedTask = (taskId) => {
    if (taskId === false) {
      setSelectedRolledBackTask(null);
      onTaskStatusChange(false);
      return;
    }

    const task = approvedTasks.find((t) => t._id === taskId);
    if (task) {
      setSelectedRolledBackTask(task);
      onTaskStatusChange(true);
    } else {
      console.error("Task not found with ID:", taskId);
      toast.error("Task not found", {
        className: "gradient-toast",
        bodyClassName: "gradient-toast-body",
        progressClassName: "gradient-toast-progress",
      });
    }
  };

  return (
    <>
      {/* Second Half - Approved Tasks */}
      {showSecondHalf && (
        <>
          <div className="task-approved-row">
            <div className="approve-title"> Approved & Yet to launch 🚀</div>
            <div className="task-arrows">
              <img
                src={backicn}
                alt=""
                className="assign-task-btn back"
                onClick={approvedTasks.length > 4 ? scrollApprovedTasksLeft : undefined}
                style={{
                  visibility: approvedTasks.length > 4 ? 'visible' : 'hidden',
                  pointerEvents: approvedTasks.length > 4 ? 'auto' : 'none'
                }}
              />
              {hasApprovedTasks ? (
                <div
                  className="task-approved-row-container"
                  ref={approvedTasksScrollRef}
                >
                  {approvedTasks.map((task) => (
                    <div
                      className="yet-task-box-wrapper"
                      key={task._id}
                      onClick={() => completedTask(task._id)}
                    >
                      <div className="yet-task-box">
                        <div className="inner-triangle-container">
                          <div className="yet-task-id">
                            <div className="yet-id">#{task.taskId}</div>
                          </div>
                          <button
                            className="yet-preview-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              completedTask(task._id);
                            }}
                          >
                            Preview
                          </button>
                          <div className="yet-task-title">{task.title}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="temp-row-container yet">
                  <div className="yet-temp-img"> </div>
                  <div className="temp-title">
                    Task nearly complete? Let's check it off!
                  </div>
                </div>
              )}

              <img
                src={nexticn}
                alt=""
                className="assign-task-btn next"
                onClick={approvedTasks.length > 4 ? scrollApprovedTasksRight : undefined}
                style={{
                  visibility: approvedTasks.length > 4 ? 'visible' : 'hidden',
                  pointerEvents: approvedTasks.length > 4 ? 'auto' : 'none'
                }}
              />
            </div>
          </div>
        </>
      )}

      {/* Approved Task Detail View */}
      {showCompletedTaskStatus && selectedRolledBackTask && (
        <div className="yet-show-task-status-container">
          <div className=" close-button-row">
            <img
              onClick={() => completedTask(false)}
              width="48"
              height="48"
              src="https://img.icons8.com/fluency/48/delete-sign.png"
              alt="delete-sign"
              className="status-cross-symbol"
            />
          </div>
          <div className="status-top-floor">
            <div className="status-top-floor-row-1">
              <div className="status-col-1">
                <div className="status-row client-padding">
                  <div className="status-task-id">
                    <div className="task-id">Task ID</div>
                    <div className="task-id-number">
                      #{selectedRolledBackTask?.taskId}
                    </div>
                  </div>
                  <div
                    className="task-current-status"
                    style={getStatusClass(selectedRolledBackTask.currentStatus)}
                  >
                    {selectedRolledBackTask.currentStatus}
                  </div>
                  <div className="status-task-title">
                    <div className="task-title-status">Task Title</div>
                    <div className="task-title-name-status task-h">
                      {selectedRolledBackTask?.title}
                    </div>
                  </div>
                  {selectedRolledBackTask.taskpurpose && (
                    <div className="status-task-title">
                      <div className="task-title-status">Task Objective</div>
                      <div className="task-title-name-status">
                        {selectedRolledBackTask?.taskpurpose}
                      </div>
                    </div>
                  )}
                  {selectedRolledBackTask.headline && (
                    <div className="status-task-title">
                      <div className="task-title-status">Main Headline</div>
                      <div className="task-title-name-status">
                        {selectedRolledBackTask?.headline}
                      </div>
                    </div>
                  )}
                  {selectedRolledBackTask.calltoaction && (
                    <div className="status-task-title">
                      <div className="task-title-status ">Tagline for Lead</div>
                      <div className="task-title-name-status">
                        {selectedRolledBackTask?.calltoaction}
                      </div>
                    </div>
                  )}
                  {selectedRolledBackTask.description && (
                    <div className="status-task-des">
                      <div className="task-title-status">Description</div>
                      <div className="task-title-name-status">
                        {selectedRolledBackTask?.description}
                      </div>
                    </div>
                  )}
                  {/* Third row: Show recurrence details only if task is recurring */}
                  {selectedRolledBackTask?.recurrence?.isRecurring && (
                    <div className="for-date-row ">
                      <div className="status-task-dates status-recurring-type">
                        <label className="task-title-status">
                          Recurrence Type
                        </label>
                        <div className="status-target-date recurring-type-div ">
                          <div className="task-title-name-status">
                            {formatRecurrencePattern(
                              selectedRolledBackTask.recurrence
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {(selectedRolledBackTask.platform_x ||
                    selectedRolledBackTask.platform_fb ||
                    selectedRolledBackTask.platform_insta ||
                    selectedRolledBackTask.platform_li ||
                    selectedRolledBackTask.platform_via_mail) && (
                    <div className="platform-row-response">
                      <label className="task-title-status">
                        Selected Platforms has to Post:
                      </label>
                      <div className="social-checkbox">
                        {selectedRolledBackTask.platform_x && (
                          <img src={x} className="checkbox__textwrapper" />
                        )}
                        {selectedRolledBackTask.platform_fb && (
                          <img src={fb} className="checkbox__textwrapper" />
                        )}
                        {selectedRolledBackTask.platform_insta && (
                          <img src={insta} className="checkbox__textwrapper" />
                        )}
                        {selectedRolledBackTask.platform_li && (
                          <img src={li} className="checkbox__textwrapper" />
                        )}
                        {selectedRolledBackTask.platform_via_mail && (
                          <img src={mail} className="checkbox__textwrapper" />
                        )}
                      </div>
                    </div>
                  )}{" "}
                  {/* First row: Always show Submission Date and Target Posting Date */}
                  <div className="for-date-row">
                    <div className="status-task-dates">
                      <label className="task-title-status">
                        Submission Date
                      </label>
                      <div className="status-deadline-date">
                        <input
                          type="date"
                          value={formatDateForDisplay(
                            selectedRolledBackTask?.submissionForReview
                          )}
                          readOnly
                        />
                      </div>
                    </div>
                    <div className="status-task-dates">
                      <label className="task-title-status">
                        Target Posting Date
                      </label>
                      <div className="status-target-date">
                        <input
                          type="date"
                          name="targetPostingDate"
                          value={formatDateForDisplay(
                            selectedRolledBackTask?.targetPostingDate
                          )}
                          readOnly
                        />
                      </div>
                    </div>
                  </div>
                  {/* Second row: Show recurrence end date only if task is recurring */}
                  {selectedRolledBackTask?.recurrence?.isRecurring && (
                    <div className="for-date-row">
                      <div className="status-task-dates">
                        <label className="task-title-status">
                          Recurrence Start Date
                        </label>
                        <div className="status-deadline-date">
                          <input
                            type="date"
                            value={formatDateForDisplay(
                              selectedRolledBackTask?.recurrence
                                ?.recurrenceStartDate
                            )}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="status-task-dates">
                        <label className="task-title-status">
                          Recurrence End Date
                        </label>
                        <div className="status-deadline-date">
                          <input
                            type="date"
                            value={formatDateForDisplay(
                              selectedRolledBackTask?.recurrence
                                ?.recurrenceEndDate
                            )}
                            readOnly
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedRolledBackTask.referenceImage && (
                    <div className="info-group-img">
                      <label className="task-title-status">
                        Reference Image
                      </label>
                      <a
                        href={selectedRolledBackTask.referenceImage}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src={selectedRolledBackTask.referenceImage}
                          alt="Brand Relate Image 6"
                          className="profile-logo"
                        />
                      </a>
                    </div>
                  )}
                </div>
              </div>
              {selectedRolledBackTask.rollbackFeedback && selectedRolledBackTask.rollbackFeedback.length > 0 && (
                <div className="past-rollback">
                  <h3 className="rollback-title">Rollback Feedback</h3>
                  <ul>
                    {selectedRolledBackTask.rollbackFeedback.map(
                      (feedback, index) => (
                        <li key={index}>
                          {feedback.clientSuggestImage ? (
                            <div className="info-group-img">
                              <a
                                href={feedback.clientSuggestImage}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <img
                                  src={feedback.clientSuggestImage}
                                  alt="Brand Relate Image 6"
                                  className="profile-logo"
                                />
                              </a>
                            </div>
                          ) : (
                            <div className="info-group-img">
                              <a
                                href={feedback.doneTaskImage}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <img
                                  src={feedback.doneTaskImage}
                                  alt="Brand Relate Image 6"
                                  className="profile-logo"
                                />
                              </a>
                            </div>
                          )}
                          {((feedback?.feedback ?? '').toString().trim().length > 0) && (
                            <div className="feed-index">
                              <div className="feedback-number">
                                {index + 1})
                              </div>
                              <div className="feedback-text">
                                {formatFeedbackText(feedback.feedback, `approved-feedback-${index}`)}
                              </div>
                            </div>
                          )}
                        </li>
                      )
                    )}
                  </ul>
                </div>
              )}
              <div className="status-col-2">
                <div className="status-title-preview">Task Preview</div>
                <div className="task-preview-img-container">
                  <img
                    src={selectedRolledBackTask?.doneTaskImage}
                    className="yet-task-preview-img"
                    alt=""
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
